import { PermissionsDirectiveModule } from '@b2b/common/directives/permissions';
import { AsyncFilterModule, parseUrl, ParseUrlFilterModule } from '@b2b/common/filters';
import { NoticesComponentModule } from '@b2b/common/components/notices';
import { Notice, NoticeFactory } from '@b2b/common/factories/notice';
import { ACMETenantResponse, TenantData, ThemesData } from '@b2b/common/resolvers';
import { safeApply } from '@b2b/common/rxjs';
import { ModalService, ModalServiceModule } from '@b2b/common/services';
import { ApiService } from '@b2b/common/services/api/types';
import { CalendarService } from '@b2b/common/services/calendar';
import { FeatureService, FeatureServiceModule } from '@b2b/common/services/feature/feature.service';
import { arrayConcat } from '@b2b/common/services/helpers';
import { PermissionsService } from '@b2b/common/services/permissions';
import { ThemesHelperService } from '@b2b/common/services/themesHelper';
import { UserPreferencesService } from '@b2b/common/services/userPreferences/types';
import { ensureArray } from '@b2b/common/utils/coerce-array';
import { ACMEResponse, TODO } from '@b2b/types/core';
import { Order } from '@b2b/types/order';
import uiRouter, { StateService } from '@uirouter/angularjs';
import angular, { IFilterService, IQService, IScope, ITimeoutService, IWindowService } from 'angular';
import { OrdersDetailServiceFactory, OrdersDetailServiceModule } from '../services/orders-detail.service';
import { GiftCardActivateModalControllerModule } from './GiftCardActivateModalController';
import {
  getGiftCardMaskedNumber,
  getGiftCardStatusBadgeClass,
  getGiftCardStatusLabel,
  getGiftCardTypeLabel,
  isGiftCardActive,
  isGiftCardResendable,
  isGiftCardStuck,
  isGiftCardsEvent,
  partitionAddonsAndFeesLast,
} from '../../utils';

const GIFT_CARD_ACTIVATION_FAILED = 'Activation failed. Please try again.';
const GIFT_CARD_RESEND_FAILED = 'Failed to resend gift card email. Please try again.';
const GIFT_CARD_RESEND_SENT = 'Gift card email sent.';

OrdersDetailController.$inject = [
  '$scope',
  '$state',
  '$filter',
  '$timeout',
  '$q',
  '$window',
  'ApiService',
  'CalendarService',
  'OrderService',
  'PermissionsService',
  'HelpersService',
  'OrderData',
  'ThemesData',
  'UserPreferencesService',
  'ThemesHelperService',
  'FeatureService',
  'OrdersDetailServiceFactory',
  'TenantData',
  'ModalService',
  'NoticeFactory',
];

function OrdersDetailController(
  $scope: IScope & { ordersDetail: TODO },
  $state: StateService,
  $filter: IFilterService,
  $timeout: ITimeoutService,
  $q: IQService,
  $window: IWindowService,
  ApiService: ApiService,
  CalendarService: CalendarService,
  OrderService: TODO,
  PermissionsService: PermissionsService,
  HelpersService: TODO,
  OrderData: { data: ACMEResponse<Order> },
  ThemesData: { data: ACMEResponse<ThemesData> },
  UserPreferencesService: UserPreferencesService,
  ThemesHelperService: ThemesHelperService,
  FeatureService: FeatureService,
  OrdersDetailServiceFactory: OrdersDetailServiceFactory,
  TenantData: { data: ACMETenantResponse<TenantData> },
  modalService: ModalService,
  NoticeFactory: NoticeFactory,
) {
  const ordersDetailService = OrdersDetailServiceFactory.createInstance({
    onOnlinePaymentLinkChanged: (params) => {
      self.onlinePaymentLinkCopied = false;
      self.onlinePaymentLink = self.generateOnlinePaymentLink(params.onlinePaymentUUID);
      $scope.$apply();
    },
  });

  const ctrl = this;

  ctrl.vm = ordersDetailService.getSnapshot();

  ordersDetailService.vm$
    .pipe(
      safeApply($scope, {
        next: (vm) => {
          ctrl.vm = vm;
        },
      }),
    )
    .subscribe();

  const self = {
    title: 'Order Details',

    model: {} as Order,
    notes: '',
    dateFormat: UserPreferencesService.getDateFormat('standardDate', 'dateFormat'),
    hourFormat: UserPreferencesService.getDateFormat('standardTime', 'hourFormat'),
    dateTimeFormat: UserPreferencesService.getDateTimeFormat('sortDateTime'),
    isSaving: false,
    isEditingVoucher: false,
    hasMembership: false,
    checkNumber: '',
    voucherNumber: '',
    totalRetail: 0,
    totalTicketQuantity: 0,
    totalAddons: 0,
    totalMemberships: 0,
    totalGiftCards: 0,
    totalPaid: 0,
    isTaxExempt: false,
    showMembershipQuantity: true,
    billingSameAsOrderContact: false,

    tenantData: {} as TenantData,
    onlinePaymentLink: '',
    onlinePaymentLinkCopied: false,
    giftCardActivationNotice: new NoticeFactory() as Notice,
    /* Keyed by order item id rather than a single flag: an order can hold several gift cards
     * (one row each - see the 1 order item : 1 purchased card mapping), and a shared flag
     * would spin every stuck row's button at once. */
    giftCardActivationInProgress: {} as Record<string, boolean>,
    // DEV-14441: same per-order-item keying, for the Resend Email button.
    giftCardResendInProgress: {} as Record<string, boolean>,

    refundingGiftCardItemIds: {} as Record<string, boolean>,
    giftCardRefundErrorItemIds: {} as Record<string, boolean>,

    paymentMethods: {
      0: 'CreditCard',
      1: 'Voucher',
      2: 'Check',
      3: 'Other',
    } as const,

    secondaryContacts: [] as TODO[],

    membershipIds: [] as string[],

    getItemType: function (type) {
      return OrderService.getItemType(type, true);
    },

    orderDetails: {
      columns: 1,
      data: 'ordersDetail.model',
      include: [
        'orderNumber',
        'formattedCreatedOn',
        'legacy',
        'secondaryContactFirstName',
        'secondaryContactLastName',
        'secondaryContactEmail',
        'checkInStatus',
        'orderRequestLink',
      ],
      mappings: {
        formattedCreatedOn: 'Order Date',
        legacy: 'Order Type',
        secondaryContactFirstName: 'Contact First Name',
        secondaryContactLastName: 'Contact Last Name',
        secondaryContactEmail: 'Contact Email',
        orderRequestLink: 'Order Request ID',
      },
    },

    orderNotes: {
      columns: 1,
      data: 'ordersDetail.model',
      include: ['notes'],
      mappings: {
        notes: 'Order Notes',
      },
    },

    paymentDetails: {
      columns: 1,
      data: 'ordersDetail.model',
      include: [
        'saleChannel',
        'paymentMethodName',
        'ccLastFourDigits',
        'creditCardBrand',
        'paymentId',
        'refundStatus',
        'orderBalance',
        'invoiceAfterDesc',
      ],
      mappings: {
        paymentId: '',
        paymentMethodName: 'Payment Method',
        ccLastFourDigits: 'Card Last 4 Digits',
        invoiceAfterDesc: 'Invoice After Event',
      },
      filters: {
        orderBalance: function (value) {
          return $filter('currency')(value);
        },
      },
    },

    customerDetails: {
      columns: 1,
      hideLabels: true,
      data: 'ordersDetail.model',
      include: ['customerLink', 'customerEmail', 'customerPhone'],
      mappings: {},
    },

    billingDetails: {
      columns: 1,
      hideLabels: true,
      data: 'ordersDetail.model',
      include: ['billingFullName', 'billingEmail', 'billingPhoneNumber'],
      mappings: {
        billingFullName: 'Name',
        billingEmail: 'Email',
        billingPhoneNumber: 'Phone Number',
      },
    },

    orderContactDetails: {
      columns: 1,
      hideLabels: true,
      data: 'ordersDetail.model',
      include: ['contactFullName', 'emailHref', 'phoneNumber'],
      mappings: {
        contactFullName: 'Name',
        emailHref: 'Email',
      },
    },

    membershipsDetails: {
      columns: 1,
      hideLabels: true,
      include: ['membershipName', 'membershipPrimaryCardholder', 'membershipExpirationDate'],
    },

    accountDetails: {
      columns: 1,
      data: 'ordersDetail.model',
      include: ['accountLink', 'companyName'],
      mappings: {
        accountLink: 'Organization Name',
      },
    },

    orderLanguages: {
      columns: 1,
      data: 'ordersDetail.model',
      include: ['preferredLanguage'],
    },

    init: function () {
      $scope.$on('$destroy', () => {
        ordersDetailService.destroy();
      });

      $scope.$emit('Application::contentTitleChange', {
        title: self.title,
      });

      if (OrderData && OrderData.data.success) {
        self.success.init(OrderData);
      } else {
        self.error.init(OrderData);
      }

      if (ThemesData && ThemesData.data.success) {
        ThemesHelperService.setModel(ThemesData.data.payload);
        self.showMembershipQuantity = ThemesHelperService.getCompany('membershipSales') as TODO;
      }

      if (TenantData && TenantData.data.success) {
        self.tenantData = TenantData.data.tenant;
      }

      $scope.$on('ResendEmail::Send', self.onResendEmailSend);
      $scope.$on('UserPreferencesService:onDateFormatLoad', self.onDateFormatLoad);
    },

    initHeaderButtons: function () {
      const contentTitle = {
        title: self.title,
        buttonLeft: {
          label: 'Back',
          action: function () {
            window.history.go(-1);
          },
        },
        buttonRight: {
          label: 'Edit Order',
          permissions: ['ORDER_UPDATE'],
          action: function () {
            $state.go('application.orders.edit', { orderId: self.model.orderId });
          },
          isDisabled: self.isDisabled(),
        },
      };

      $scope.$emit('Application::contentTitleChange', contentTitle);
    },

    hasName: function (order) {
      return order.contactFirstName || order.contactLastName;
    },

    getAccountContacts: function (id) {
      ApiService.partners.contacts
        .list(id, HelpersService.getPaginator())
        .then((response) => self.success.getAccountContacts(response))
        .catch(() => self.error.getAccountContacts());
    },

    findContact: function (id) {
      let result;

      for (let i = 0, ii = self.secondaryContacts.length; i < ii; i++) {
        if (self.secondaryContacts[i].id === id) {
          result = self.secondaryContacts[i];
          break;
        }
      }
      return result;
    },

    isMembership: function (event) {
      return event.eventId.toLowerCase() === 'membership';
    },

    // DEV-13553: the synthetic "Gift Cards" event group (gift cards sold as a product),
    // distinct from gift-card redemption (paying with a gift card, out of scope here).
    // Thin delegate to the tested pure helper, as with the display helpers below.
    isGiftCards: function (event) {
      return isGiftCardsEvent(event);
    },

    // Binary on ISSUED (Q7): every other status — known or future — fails closed to Inactive.
    // Logic lives in gift-card-display.utils.ts (testable pure helper); this is a thin
    // delegate so summary.tpl.html's ordersDetail.isGiftCardActive(...) binding keeps working.
    isGiftCardActive: function (status) {
      return isGiftCardActive(status);
    },

    isGiftCardStuck: function (status) {
      return isGiftCardStuck(status);
    },

    // DEV-14441: DIGITAL + ISSUED (tested helper) and the user may resend at all. The backend
    // enforces both permissions too.
    isGiftCardResendable: function (ticket) {
      return isGiftCardResendable(ticket?.giftCardDetail) && PermissionsService.check('GIFT_CARD_EMAIL_SEND');
    },

    // DEV-14441: without this, the resend modal shows the stored recipient read-only.
    canUpdateGiftCardRecipient: function () {
      return PermissionsService.check('GIFT_CARD_EMAIL_RECIPIENT_UPDATE');
    },

    getGiftCardStatusLabel: function (status) {
      return getGiftCardStatusLabel(status);
    },

    getGiftCardStatusBadgeClass: function (status) {
      return getGiftCardStatusBadgeClass(status);
    },

    // Title-cases the backend's uppercase giftCardType ("PHYSICAL"/"DIGITAL") for display.
    getGiftCardTypeLabel: function (giftCardType) {
      return getGiftCardTypeLabel(giftCardType);
    },

    // Card Number column: only the last four are ever available (no PIN/full number stored).
    getGiftCardMaskedNumber: function (lastFour) {
      return getGiftCardMaskedNumber(lastFour);
    },

    isRefundingGiftCard: function (ticket) {
      return !!self.refundingGiftCardItemIds[ticket.orderItemId];
    },

    hasGiftCardRefundError: function (ticket) {
      return !!self.giftCardRefundErrorItemIds[ticket.orderItemId];
    },

    confirmRefundGiftCard: function (ticket) {
      modalService
        .confirm({
          title: 'Remove gift card and issue refund?',
          message:
            'This removes the gift card from the order and refunds its full value to the original payment method. This cannot be undone.',
          okText: 'Remove & Refund',
        })
        .then((confirmed) => {
          if (confirmed) {
            self.refundGiftCard(ticket);
          }
        });
    },

    refundGiftCard: function (ticket) {
      self.refundingGiftCardItemIds[ticket.orderItemId] = true;
      self.giftCardRefundErrorItemIds[ticket.orderItemId] = false;

      return ApiService.refunds
        .refund({
          orderId: self.model.orderId,
          noEmail: false,
          notes: '',
          refundItems: [{ orderItemId: ticket.orderItemId, quantity: ticket.quantity }],
        })
        .then(
          () => {
            $state.go($state.current, { orderId: $state.params.orderId }, { reload: true });
          },
          () => {
            self.refundingGiftCardItemIds[ticket.orderItemId] = false;
            self.giftCardRefundErrorItemIds[ticket.orderItemId] = true;
          },
        );
    },

    isMembershipConversion: function (ticket) {
      return ticket && ticket.conversionStatus && ticket.conversionStatus === 'MembershipConversion';
    },

    getOrderMembershipId: function (membership) {
      let result = '';

      if (membership.itemDetailId) {
        result = membership.itemDetailId;
      }

      return result;
    },

    getOrderMembershipLevelName: function (membershipItem) {
      let result = '';

      if (membershipItem.membershipLevelName) {
        result = membershipItem.membershipLevelName + ': ' + (membershipItem.itemTypeName || '');
      } else {
        result = membershipItem.itemTypeName || '';
      }

      return result;
    },

    getOrderBalance: function () {
      return HelpersService.currencyFloatToInteger(self.model.orderBalance || 0);
    },

    processTicketQuantity: function () {
      const order = self.model;

      for (let i = 0, ii = order.eventItems.length; i < ii; i++) {
        if (order.eventItems[i].items instanceof Array) {
          for (let j = 0, jj = order.eventItems[i].items.length; j < jj; j++) {
            self.determineTicketType(order.eventItems[i].items[j]);
          }
        }
      }
    },

    determineTicketType: function (item) {
      let type;

      switch (item.itemType) {
        case 'Inventory':
        case 'Event':
          if (item.itemType === 'Inventory') {
            type = 'totalAddons';
          } else if (item.itemType === 'Event') {
            type = 'totalTicketQuantity';
          }
          if (item && item.tickets instanceof Array) {
            for (let i = 0, ii = item.tickets.length; i < ii; i++) {
              if (item.tickets[i].ticketStatus.toLowerCase() === 'normal') {
                self[type]++;
              }
            }
          }
          break;
        case 'MembershipPurchase':
        case 'MembershipRenewal':
          self.totalMemberships++;
          break;
        case 'GiftCard':
          // Gift cards have no `tickets` array (quantity is always 1 per card,
          // backend-enforced), so count `quantity` directly rather than the
          // ticket-status loop used for Event/Inventory above.
          self.totalGiftCards += item.quantity || 0;
          break;
      }
    },

    getRetail: function () {
      const order = self.model;
      let result = 0;

      for (const eventItems of order.eventItems) {
        for (const ticket of ensureArray(eventItems.items)) {
          const price = self.isDiscounted(ticket)
            ? HelpersService.currencyFloatToInteger(ticket.discountedSubtotal)
            : HelpersService.currencyFloatToInteger(ticket.subtotal);

          result = result + price;
        }
      }

      self.totalRetail = HelpersService.integerToCurrencyFloat(result);

      return self.totalRetail;
    },

    // The tax-excluded unit price to display for an item: `basePrice` when the
    // order carries sales-tax fields, otherwise the regular (tax-inclusive) price.
    getItemPrice: function (ticket) {
      return ticket.basePrice !== undefined && ticket.basePrice !== null ? ticket.basePrice : ticket.unitPrice;
    },

    // Paired with getItemPrice: once an item is in "base price" mode, its discounted
    // price must also come from the base-price space (falling back to basePrice
    // itself when there's no discount), never from the tax-inclusive fields — mixing
    // the two spaces produces a bogus tax/discount amount.
    getItemDiscountedPrice: function (ticket) {
      if (ticket.basePrice === undefined || ticket.basePrice === null) {
        return ticket.discountedUnitPrice;
      }
      return ticket.discountedBasePrice !== undefined && ticket.discountedBasePrice !== null
        ? ticket.discountedBasePrice
        : ticket.basePrice;
    },

    // Line total for getItemPrice. Falls back to the server-provided `subtotal`
    // (rather than recomputing quantity * unitPrice) so membership conversions,
    // prorations, and partial refunds keep reflecting the order's actual money.
    getItemSubtotal: function (ticket) {
      if (ticket.basePrice === undefined || ticket.basePrice === null) {
        return ticket.subtotal;
      }
      const quantity = ticket.quantity || 0;
      const priceInt = HelpersService.currencyFloatToInteger(ticket.basePrice || 0);
      return HelpersService.integerToCurrencyFloat(quantity * priceInt);
    },

    // Line total for getItemDiscountedPrice.
    getItemDiscountedSubtotal: function (ticket) {
      if (ticket.basePrice === undefined || ticket.basePrice === null) {
        return ticket.discountedSubtotal;
      }
      const quantity = ticket.quantity || 0;
      const priceInt = HelpersService.currencyFloatToInteger(self.getItemDiscountedPrice(ticket) || 0);
      return HelpersService.integerToCurrencyFloat(quantity * priceInt);
    },

    /**
     * Computes the order money breakdown. Subtotal/Discount are tax-excluded
     * (matching the item rows); Tax is the gap between that and the real,
     * tax-inclusive amount charged (same subtotal/discountedSubtotal pairing as
     * getRetail). Subtotal - Discount + Tax always reconciles to that real total,
     * including for tax-exempt orders, which naturally net to zero tax since their
     * discounted price never carried a tax component to begin with.
     */
    getOrderTotals: function () {
      const order = self.model;
      let baseTotal = 0;
      let discountedBaseTotal = 0;
      let total = 0;

      for (const eventItems of order.eventItems) {
        for (const ticket of ensureArray(eventItems.items)) {
          baseTotal += HelpersService.currencyFloatToInteger(self.getItemSubtotal(ticket) || 0);
          discountedBaseTotal += HelpersService.currencyFloatToInteger(self.getItemDiscountedSubtotal(ticket) || 0);
          total += self.isDiscounted(ticket)
            ? HelpersService.currencyFloatToInteger(ticket.discountedSubtotal || 0)
            : HelpersService.currencyFloatToInteger(ticket.subtotal || 0);
        }
      }

      return {
        subtotal: HelpersService.integerToCurrencyFloat(baseTotal),
        discount: HelpersService.integerToCurrencyFloat(baseTotal - discountedBaseTotal),
        tax: HelpersService.integerToCurrencyFloat(total - discountedBaseTotal),
      };
    },

    getSubtotal: function () {
      return self.getOrderTotals().subtotal;
    },

    getDiscount: function () {
      return self.getOrderTotals().discount;
    },

    getTax: function () {
      return self.getOrderTotals().tax;
    },

    allowCancel: function () {
      return self.model.refundStatus !== 'Fully Refunded' && !self.hasMembership;
    },

    isRefunded: function () {
      return self.model.refundStatus === 'Fully Refunded' || self.isChargedBack();
    },

    isChargedBack: function () {
      return self.model.refundStatus === 'Charged Back';
    },

    isDisabled: function () {
      return self.isChargedBack();
    },

    isDiscounted: function (ticket) {
      const subtotal = HelpersService.currencyFloatToInteger(ticket.subtotal);
      const discountedSubtotal = HelpersService.currencyFloatToInteger(ticket.discountedSubtotal);

      return subtotal !== discountedSubtotal;
    },

    getPaid: function () {
      let result = 0;
      const order = self.model;
      const balance = self.getOrderBalance();

      for (let i = 0, ii = order.eventItems.length; i < ii; i++) {
        if (order.eventItems[i].items instanceof Array) {
          for (let j = 0, jj = order.eventItems[i].items.length; j < jj; j++) {
            result += HelpersService.currencyFloatToInteger(order.eventItems[i].items[j].discountedSubtotal || 0);
          }
        }
      }

      result = result - balance;

      /**
       * Multiplying by -1 to display a negative number
       */
      self.totalPaid = HelpersService.integerToCurrencyFloat(result) * -1;

      return result;
    },

    getCouponCode: function (ticket) {
      let result = '';
      if (ticket && ticket.couponDetail && ticket.couponDetail.code) {
        result = ticket.couponDetail.code;
      }
      return result;
    },

    hasPurchasedItems: function (items) {
      let result = 0;

      if (items instanceof Array) {
        for (let i = 0, ii = items.length; i < ii; i++) {
          result += items[i].quantity || 0;
        }
      }

      return result > 0;
    },

    checkContacts: function (order: Order) {
      if (!order) {
        return false;
      }

      const billingContactInfo =
        (order.billingFirstName || '') +
        (order.billingLastName || '') +
        (order.billingEmail || '') +
        (order.billingPhoneNumber || '');

      const orderContactInfo =
        (order.contactFirstName || '') +
        (order.contactLastName || '') +
        (order.email || '') +
        (order.phoneNumber || '');

      return billingContactInfo === orderContactInfo;
    },

    processOrderLinks: function (order) {
      if (order.accountName && order.accountId) {
        order.accountLink = parseUrl(
          $state.href('application.partners.detail', { id: order.accountId }),
          order.accountName,
        );
      } else {
        order.accountLink = '';
      }

      if (order.orderRequestId) {
        order.orderRequestLink = parseUrl(
          $state.href('application.orders.requests.edit', { id: order.orderRequestId }),
          order.orderRequestId,
        );
      } else {
        order.orderRequestLink = '';
      }

      return order;
    },

    sortOrderEvents: function (a, b) {
      const eventId = a.eventId.toLowerCase();
      if (eventId !== 'addons' && eventId !== 'fees') {
        return -1;
      }

      return 0;
    },

    process: function (order: Order) {
      let hasMembership = false;
      const hasMembershipConversion = false;

      order = self.processOrderLinks(order);

      order.memberships = [];

      if (order.membershipId && order.membershipId !== 'null') {
        self.membershipIds.push(order.membershipId);
      }

      self.billingSameAsOrderContact = self.checkContacts(order);

      order.billingFullName = arrayConcat([order.billingFirstName, order.billingLastName], ' ');
      order.contactFullName = arrayConcat([order.contactFirstName, order.contactLastName], ' ');
      order.emailHref = order.email;

      if (FeatureService.isEnabled(FeatureService.customers) && order.customerId) {
        const href = $state.href('application.customers.individuals.detail', { id: order.customerId });

        ApiService.customers
          .detail({ customerId: order.customerId })
          .then((response) => response.data.payload)
          .then((customer) => {
            const customerFullName = [customer.firstName, customer.lastName].filter(Boolean).join(' ');

            order.customerLink = PermissionsService.check('CUSTOMERS_VIEW')
              ? parseUrl(href, customerFullName)
              : customerFullName;
            order.customerEmail = customer.email;
            order.customerPhone = customer.phoneNumber ?? '';
          });
      }

      order.invoiceAfterDesc = order.invoiceAfter ? 'Yes' : 'No';

      order.eventItems = partitionAddonsAndFeesLast(ensureArray(order.eventItems));

      // Merge fee items into the Addons event so they display together
      const feesEventIndex = order.eventItems.findIndex((e) => e.eventId === 'Fees');
      const addonsEventIndex = order.eventItems.findIndex((e) => e.eventId === 'Addons');
      if (feesEventIndex !== -1 && addonsEventIndex !== -1) {
        const feesEvent = order.eventItems[feesEventIndex];
        if (feesEvent.items instanceof Array) {
          order.eventItems[addonsEventIndex].items = ensureArray(order.eventItems[addonsEventIndex].items).concat(
            feesEvent.items,
          );
        }
        order.eventItems.splice(feesEventIndex, 1);
      }

      for (let i = 0, ii = order.eventItems.length; i < ii; i++) {
        if (order.eventItems[i].eventDate) {
          order.eventItems[i].startDate = CalendarService.toFormat(
            CalendarService.parseDate(order.eventItems[i].eventDate),
            UserPreferencesService.getDateFormat('shortDate', 'dateFormat'),
          );
          order.eventItems[i].startHour = CalendarService.toFormat(
            CalendarService.parseDate(order.eventItems[i].eventDate),
            UserPreferencesService.getDateFormat('standardTime', 'hourFormat'),
          );
        } else {
          order.eventItems[i].startDate = 'Date N/A';
          order.eventItems[i].startHour = 'Time N/A';
        }

        if (order.eventItems[i].state === 'cancelled') {
          order.eventItems[i].isCancelled = true;
          order.eventItems[i].formattedCancellationDate = CalendarService.toFormat(
            order.eventItems[i].cancellationDate,
            HelpersService.dateFormats.standardDateTime,
          );
        } else {
          order.eventItems[i].isCancelled = false;
          order.eventItems[i].formattedCancellationDate = '';
        }

        // TODO: b2bAddEventToOrder - remove this? trying to remove extra dynamic fields
        if (order.eventItems[i].state === 'suspended') {
          order.eventItems[i].isSuspended = true;
        }

        if (!hasMembership) {
          hasMembership = self.isMembership(order.eventItems[i]);
        }

        if (!order.membershipId && self.isMembership(order.eventItems[i])) {
          self.setMembershipIds(order.eventItems[i]);
        }

        // This is an event, so check if it has atleast 1 converted ticket
        if (!hasMembership && !hasMembershipConversion && order.eventItems[i].items instanceof Array) {
          for (let j = 0, jj = order.eventItems[i].items.length; j < jj && !hasMembershipConversion; j++) {
            if (
              order.eventItems[i].items[j] &&
              order.eventItems[i].items[j].tickets instanceof Array &&
              order.eventItems[i].items[j].tickets.length
            ) {
              order.eventItems[i].hasMembershipConversion = self.isMembershipConversion(order.eventItems[i].items[j]);
              if (order.eventItems[i].hasMembershipConversion) {
                break;
              }
            }
          }
        }
      }

      self.hasMembership = hasMembership;

      order.emailLanguages = {
        code: 'en-us',
        name: 'English (United States)',
      };

      order.preferredLanguage = order.emailLanguages.name;

      order.formattedCreatedOn = CalendarService.toFormat(
        CalendarService.parseDate(order.createdOn),
        self.dateTimeFormat,
      );

      return order;
    },

    setMembershipIds: function (order) {
      order.items.forEach(function (item) {
        self.membershipIds.push(item.itemDetailId);
      });
    },

    hasMembershipCancelItem: function (model) {
      return HelpersService.path(model, ['eventItems'], []).some((eventItem) => {
        return HelpersService.path(eventItem, ['items'], []).some((item) => {
          return item.itemType === 'MembershipCancel';
        });
      });
    },

    getMembershipData: function () {
      const uniqueIds = Array.from(new Set(self.membershipIds));
      const membershipPromises = uniqueIds.map((item) => ApiService.membership.detail(item));

      return $q.all(membershipPromises).then((responses: any[]) => {
        const memberships = responses.map((response) => {
          const membership = response.data.payload;

          if (membership.auxiliaryMembership) {
            return;
          }

          membership.primaryCardholder = membership.cardholders.find(function (cardholder) {
            return cardholder.cardType === 'primary';
          });

          const membershipPrimaryCardholder = arrayConcat(
            [
              'Primary:',
              membership.primaryCardholder.prefix,
              membership.primaryCardholder.firstName,
              membership.primaryCardholder.lastName,
            ],
            ' ',
          );

          const membershipName = parseUrl(
            $state.href('application.membership.detail', { id: membership.id }),
            membership.membershipCategory + ': ' + membership.offeringName,
          );

          const membershipExpirationDate =
            'Expires: ' +
            CalendarService.toFormat(
              membership.membershipExpirationDate,
              UserPreferencesService.getDateFormat('shortDate', 'dateFormat'),
            );

          self.model.memberships.push({
            membershipName: membershipName,
            membershipPrimaryCardholder: membershipPrimaryCardholder,
            membershipExpirationDate: membershipExpirationDate,
          });

          return membership;
        });

        return memberships;
      });
    },

    update: function () {
      self.model.notes = self.notes + '';
      self.isSaving = true;
      ApiService.orders
        .notes({
          id: $state.params.orderId,
          notes: self.model.notes,
        })
        .then((response) => self.success.update(response))
        .catch((response) => self.error.update(response));
    },

    updateNotes: function () {
      //need this, but it can be empty
      self.update();
    },

    cancel: function () {
      window.history.go(-1);
    },

    updateVoucher: function () {
      self.isSaving = true;
      ApiService.orders
        .voucher({
          id: $state.params.orderId,
          paymentId: self.voucherNumber,
        })
        .then((response) => self.success.updateVoucher(response))
        .catch((response) => self.error.updateVoucher(response));
    },

    sendEmail: function (email: string, preferredLanguage: string) {
      ApiService.orders
        .email({
          id: self.model.orderId,
          email: email,
          preferredLanguage: preferredLanguage,
        })
        .then((response) => self.success.sendEmail(response))
        .catch((response) => self.error.sendEmail(response));
    },

    printOrderSummary: function () {
      const height = $(window).height();
      const width = $(window).width();

      const wHeight = height ? height - 20 : 0;
      const wWidth = width ? width - 20 : 0;

      console.log('ProgramsOrdersDetail::printOrderSummary', wHeight, wWidth);
      // Open print window
      HelpersService.setCookie('printorder', {
        orderId: self.model.orderId,
        isInvoice: false,
      });

      $timeout(function () {
        window.open('/app/orders/print', self.model.orderNumber, 'height=' + wHeight + ',width=' + wWidth + ',left=10');
      }, 10);
    },

    printInvoice: function () {
      const height = $(window).height();
      const width = $(window).width();

      const wHeight = height ? height - 20 : 0;
      const wWidth = width ? width - 20 : 0;

      console.log('ProgramsOrdersDetail::printInvoice', wHeight, wWidth);
      // Open print window
      HelpersService.setCookie('printorder', {
        orderId: self.model.orderId,
        isInvoice: true,
      });

      $timeout(function () {
        window.open(
          '/app/orders/print/invoice',
          self.model.orderNumber,
          'height=' + wHeight + ',width=' + wWidth + ',left=10',
        );
      }, 10);
    },

    isState: function (state) {
      return $state.current?.name?.indexOf('application.orders.detail.' + state) === 0;
    },

    getState: function (name) {
      const stateName = 'application.orders.detail.' + name;
      $state.go(stateName, { id: $state.params.id });
    },

    getPaymentMethod: function () {
      if (/check/i.test(self.model.paymentMethodName)) {
        self.paymentDetails.mappings.paymentId = 'Check Number';
        self.checkNumber = self.model.paymentId;
      }

      if (/voucher/i.test(self.model.paymentMethodName)) {
        self.paymentDetails.mappings.paymentId = 'Voucher Number';
        self.voucherNumber = self.model.paymentId;
      }

      if (/other/i.test(self.model.paymentMethodName)) {
        self.paymentDetails.mappings.paymentId = 'Payment ID';
      }

      if (/storedvalue/i.test(self.model.paymentMethodName)) {
        self.paymentDetails.include = self.paymentDetails.include.filter((k) => k !== 'paymentId');
      }
    },

    fetchOnlinePaymentLink: function () {
      ordersDetailService.fetchOnlinePaymentLink(self.model);
    },

    createOnlinePaymentLink: function () {
      ordersDetailService.createOnlinePaymentLink(self.model);
    },

    deleteOnlinePaymentLink: function () {
      modalService
        .confirm({
          title: 'Are you sure?',
          okText: 'Delete',
          message: 'Do you want to delete this link?',
        })
        .then((confirmed) => {
          if (!confirmed) {
            return;
          }

          return ordersDetailService.deleteOnlinePayment(ctrl.vm.onlinePaymentUUID);
        });
    },

    activateGiftCard: function (ticket: any) {
      if (!ticket || !ticket.giftCardDetail || !ticket.orderItemId) {
        return;
      }

      self.giftCardActivationNotice.hideErrorMessages();

      /* A digital card's number and PIN are minted by the provider, so there is nothing to
       * collect and the request goes straight out. A physical card's are printed on the card
       * and stored nowhere (the order payload carries `lastFour` alone), so staff key them in.
       *
       * Anything other than an explicit DIGITAL prompts, rather than testing for PHYSICAL and
       * posting otherwise: sending no card number for a physical card isn't a harmless no-op -
       * the backend claims the row and marks it ERRORED_ACTIVATION before rejecting, burning an
       * activation_attempt against acme-relay's retry cap. A needless prompt is cancellable;
       * a burnt attempt isn't recoverable. */
      if (ticket.giftCardDetail.giftCardType === 'DIGITAL') {
        self.issueGiftCard(ticket);
        return;
      }

      modalService
        .open<{ cardNumber: string; pin?: string }>({
          templateUrl: 'views/application/views/orders/detail/modals/activateGiftCard.tpl.html',
          size: 'md',
          controller: 'GiftCardActivateModalController',
        })
        .result.then(
          (credentials) => self.issueGiftCard(ticket, credentials),
          () => {
            /* dismissed - nothing was sent, so there is nothing to undo or report */
          },
        );
    },

    issueGiftCard: function (ticket: any, credentials?: { cardNumber: string; pin?: string }) {
      const orderItemId = ticket.orderItemId;

      self.giftCardActivationInProgress[orderItemId] = true;

      ApiService.giftCards.purchased
        .issueByOrderItem(orderItemId, credentials)
        .then((response) => {
          self.giftCardActivationInProgress[orderItemId] = false;

          const issued = response?.data?.payload;

          /* A resolved request doesn't mean the card activated. When the provider rejects the
           * activation the backend catches it, records ERRORED_ACTIVATION and still answers
           * 200 - so without this the row would report success while still stuck. Reuses the
           * same ISSUED-or-nothing predicate the status column renders from, which also fails
           * closed on PROCESSING_ISSUE (another claimer holds the row) and any future status. */
          if (!isGiftCardActive(issued?.status)) {
            self.giftCardActivationNotice.showErrorMessages([{ message: GIFT_CARD_ACTIVATION_FAILED }]);
            return;
          }

          /* The response is the updated row, so the badge (and a digital card's newly minted
           * number) can be applied straight away rather than waiting on a refetch of the whole
           * order. The Activate button drops out on its own once the status is no longer stuck.
           * lastFour is assigned only when present because the payload omits null fields, and
           * blanking a number we already have would be a visible regression. */
          ticket.giftCardDetail.status = issued.status;
          if (issued.lastFour) {
            ticket.giftCardDetail.lastFour = issued.lastFour;
          }
        })
        .catch((error) => {
          self.giftCardActivationInProgress[orderItemId] = false;
          self.giftCardActivationNotice.showErrorMessages([
            { message: error?.data?.message || error?.statusText || GIFT_CARD_ACTIVATION_FAILED },
          ]);
        });
    },

    // DEV-14441: invoked via the resend-email directive's on-send binding, NOT the
    // 'ResendEmail::Send' event — that event is what resends the order confirmation here.
    // Growl on success, no reload: the email going out changes nothing on the page.
    resendGiftCardEmail: function (ticket: any, email: string) {
      const orderItemId = ticket?.orderItemId;
      if (!orderItemId) {
        return;
      }

      self.giftCardActivationNotice.hideErrorMessages();
      self.giftCardResendInProgress[orderItemId] = true;

      ApiService.giftCards.purchased
        .resendEmailByOrderItem(orderItemId, email)
        .then(() => {
          self.giftCardResendInProgress[orderItemId] = false;
          // The backend saves a corrected address as the new recipient; keep the next prefill in sync.
          if (email) {
            ticket.giftCardDetail.recipientEmail = email.trim();
          }
          self.giftCardActivationNotice.showSuccessMessages([{ message: GIFT_CARD_RESEND_SENT }], { duration: 3000 });
        })
        .catch((error) => {
          self.giftCardResendInProgress[orderItemId] = false;
          self.giftCardActivationNotice.showErrorMessages([{ message: error?.data?.message || GIFT_CARD_RESEND_FAILED }]);
        });
    },

    generateOnlinePaymentLink: function (onlinePaymentUUID: string): string {
      return ordersDetailService.generateOnlinePaymentLink({
        brandedUrl: ThemesHelperService.getCompany('brandedUrl') || '',
        tenantId: ThemesHelperService.getCompany('tenantId'),
        consumerPage: self.tenantData.consumerPage,
        onlinePaymentUUID,
      });
    },

    copyToClipboardOnlinePaymentLink: async function () {
      self.onlinePaymentLinkCopied = false;

      try {
        await $window.navigator.clipboard
          .writeText(self.generateOnlinePaymentLink(ctrl.vm.onlinePaymentUUID))
          .then(() => {
            self.onlinePaymentLinkCopied = true;
            $scope.$apply();
          });
      } catch (error) {
        console.error(error);
      }
    },

    success: {
      init: function (response) {
        // Check if account order or normal order for permissions
        if (response.data.payload.accountId) {
          if (!PermissionsService.check('ACCT_ORDERS_VIEW')) {
            console.log('ACCT_ORDERS_VIEW');
            $state.go('home.default', {}, { notify: true });
          }
          if (response.data.payload.accountContactId) {
            self.getAccountContacts(response.data.payload.accountId);
          }
        } else {
          if (!PermissionsService.check('ORDER_VIEW')) {
            console.log('ORDER_VIEW');
            $state.go('home.default', {}, { notify: true });
          }
        }
        self.model = self.process(response.data.payload);
        self.isTaxExempt = !!response.data.payload.isTaxExempt;

        if (self.membershipIds.length) {
          self.getMembershipData();
        }

        ordersDetailService.checkCancelMembershipEligility(self.model);

        self.fetchOnlinePaymentLink();

        self.getPaymentMethod();

        self.notes = self.model.notes ? self.model.notes + '' : '';
        self.processTicketQuantity();
        self.initHeaderButtons();
      },
      getAccountContacts: function (response) {
        self.secondaryContacts = response.data.payload.list;
        const orderSecondaryContact = self.findContact(self.model.accountContactId);
        if (orderSecondaryContact) {
          self.model.secondaryContactFirstName = orderSecondaryContact.firstName;
          self.model.secondaryContactLastName = orderSecondaryContact.lastName;
          self.model.secondaryContactEmail = orderSecondaryContact.email;
        }
      },
      update: function (response) {
        self.isSaving = false;
      },

      updateVoucher: function (response) {
        self.isSaving = false;
        self.isEditingVoucher = false;
        self.model.paymentId = self.voucherNumber;
      },
      sendEmail: function (response) {
        $state.go($state.current, { orderId: $state.params.orderId }, { reload: true });
      },
      printOrderSummary: function (response) {
        //pop up print window here
      },
    },

    error: {
      init: function (response) {
        self.isSaving = false;
        self.initHeaderButtons();
      },
      getAccountContacts: function () {},

      update: function (response) {
        self.isSaving = false;
      },

      updateVoucher: function (response) {
        self.isSaving = false;
        self.isEditingVoucher = false;
      },
      sendEmail: function (response) {},
      printOrderSummary: function (response) {},
    },

    onResendEmailSend: function (e, data) {
      self.sendEmail(data.email, data.preferredLanguage);
    },

    onDateFormatLoad: function (e, data) {
      self.dateFormat = UserPreferencesService.getDateFormat('standardDate', 'dateFormat');
      self.hourFormat = UserPreferencesService.getHourFormat();
      self.dateTimeFormat = UserPreferencesService.getDateTimeFormat('shortDateTime');
    },
  };

  $scope.ordersDetail = self;
  self.init();
}

export const OrderDetailsControllerModule = angular
  .module('acme.view.application.orders.detail.controllers', [
    uiRouter,
    'ui.bootstrap',
    'acme.common.service.api',
    'acme.common.service.order',
    'acme.common.service.calendar',
    'acme.common.service.permissions',
    'acme.common.service.helpers',
    'acme.common.service.userPreferences',
    'acme.common.service.themesHelper',
    'acme.common.factory.notice',
    'acme.common.directive.notice',
    NoticesComponentModule,
    GiftCardActivateModalControllerModule,
    FeatureServiceModule,
    AsyncFilterModule,
    ParseUrlFilterModule,
    OrdersDetailServiceModule,
    PermissionsDirectiveModule,
    ModalServiceModule,
  ])

  .controller('OrdersDetailController', OrdersDetailController).name;
